import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { client, removeAuthToken, removeUserData, setAuthToken, setUserData } from '../lib/client';
import { AxiosError } from 'axios';
import { BankName } from '../constants/banks';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { isMobile } from 'react-device-detect';

export type AuthStep = 'phone' | 'verify' | 'name';

interface UseAuthReturn {
  step: AuthStep;
  error: string | null;
  isLoading: boolean;
  sendOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, token: string) => Promise<void>;
  updateUserProfile: (data: { name: string; bank: BankName; account_number: string }) => Promise<void>;
  logout: () => void;
}

interface AuthResponse {
  success: boolean;
  message: string;
  error?: string;
}

interface VerifyResponse extends AuthResponse {
  isNewUser: boolean;
  token?: string;
  signupToken?: string;
  user?: {
    id: string;
    name: string;
    phone_number: string;
    primary_group_id: string | null;
  };
}

interface SignupResponse extends AuthResponse {
  token: string;
  user: {
    id: string;
    name: string;
    phone_number: string;
    primary_group_id: string | null;
    bank: BankName;
    account_number: string;
  };
}

export const useAuth = ({redirectUrl}: {redirectUrl?: string}): UseAuthReturn => {
  const navigate = useNavigate();
  const [step, setStep] = useState<AuthStep>('phone');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [signupToken, setSignupToken] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const sendOtp = async (phone: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const { data } = await client.post<AuthResponse>('/auth/phone', {
        phone_number: phone
      });

      if (!data.success) {
        throw new Error(data.error || '인증 코드 발송에 실패했습니다.');
      }

      setStep('verify');
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('알 수 없는 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (phone: string, token: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const { data } = await client.post<VerifyResponse>('/auth/verify', {
        phone_number: phone,
        auth_code: token,
        platform: isMobile ? 'mobile-web' : 'desktop-web'
      });

      if (!data.success) {
        throw new Error(data.error || '인증에 실패했습니다.');
      }

      if (data.isNewUser && data.signupToken) {
        // 신규 유저 - 회원가입 필요
        setSignupToken(data.signupToken);
        setStep('name');
      } else if (data.token) {
        // 기존 유저 - 로그인 완료
        setAuthToken(data.token);
        setUserData(data.user);
        navigate(redirectUrl || '/', { replace: true });
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('알 수 없는 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserProfile = async (data: { name: string; bank: BankName; account_number: string }) => {
    setError(null);
    setIsLoading(true);
    try {
      if (!signupToken) {
        throw new Error('회원가입 토큰이 없습니다.');
      }

      const { data: responseData } = await client.post<SignupResponse>('/auth/signup', {
        signupToken,
        name: data.name,
        bank: data.bank,
        account_number: data.account_number,
        platform: isMobile ? 'mobile-web' : 'desktop-web'
      });

      if (!responseData.success) {
        throw new Error(responseData.error || '회원가입에 실패했습니다.');
      }
      toast('환영합니다, ' + data.name + '님!', {
        icon: '🔥',
      });

      // 로그인 토큰 저장
      setAuthToken(responseData.token);
      setUserData(responseData.user);
      navigate(redirectUrl || '/', { replace: true });
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('알 수 없는 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setError(null);
    setIsLoading(true);
    try {
      // 로컬 스토리지에서 토큰 제거
      removeAuthToken();
      removeUserData();
      queryClient.clear();
      
      // 기본 페이지로 이동
      navigate('/auth', { replace: true });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('알 수 없는 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    step,
    error,
    isLoading,
    sendOtp,
    verifyOtp,
    updateUserProfile,
    logout,
  };
}; 